import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { Link, NavLink } from 'react-router-dom';

// English lives at /, Arabic at /ar. Copy is stored as { en, ar } pairs;
// anything without an Arabic version falls back to English.
const LangContext = createContext({ lang: 'en' });

export function LangProvider({ lang, children }) {
  return <LangContext.Provider value={{ lang }}>{children}</LangContext.Provider>;
}

export function localize(path, lang) {
  if (lang !== 'ar') return path;
  return path === '/' ? '/ar' : `/ar${path}`;
}

// Same page in the other language, e.g. /ar/work/fimka <-> /work/fimka.
export function switchLanguagePath(pathname, lang) {
  if (lang === 'ar') return pathname.replace(/^\/ar(?=\/|$)/, '') || '/';
  return localize(pathname, 'ar');
}

export function useLang() {
  const { lang } = useContext(LangContext);
  const t = useCallback(
    (value) => {
      if (value == null || typeof value !== 'object' || Array.isArray(value)) return value;
      return value[lang] ?? value.en;
    },
    [lang]
  );
  const to = useCallback((path) => localize(path, lang), [lang]);
  return useMemo(() => ({ lang, dir: lang === 'ar' ? 'rtl' : 'ltr', t, to }), [lang, t, to]);
}

export function LLink({ to, ...props }) {
  const { to: localized } = useLang();
  return <Link to={localized(to)} {...props} />;
}

export function LNavLink({ to, ...props }) {
  const { to: localized } = useLang();
  return <NavLink to={localized(to)} {...props} />;
}
